
import React from "react";
import { i18nManager } from "../../../../i18n";
import { useSearchParams } from "react-router-dom";
import { Schemes, Subscriber, Utils } from "../../../../utils";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../shared";
import { BoolHelper, ButtonManageRef, Classname, FormControl, FormGroup, ObjectHelper, Scheme, TextAreaInput } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ConfigurationType, ICountryConfiguration, CountryConfigurationAndMappingService, SanitizedResponse } from "../../../../services";
import { ConfigurationInputs, ConfigurationValue } from "./ConfigUtils";

function CountryConfigurations() {
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.ploutous.configurations.country_configs;
    const countryConfigurationAndMappingService = CountryConfigurationAndMappingService.getInstance();
    const [activeCountryConfiguration, setActiveCountryConfiguration] = React.useState<ICountryConfiguration>();
    const [countryConfigurations, setCountryConfigurations] = React.useState<ApiResponseData<ICountryConfiguration>>({});
    labels.dialogs.manage.form.find((f) => f.name === "type")!.options = i18nManager.Labels.dashboard.ploutous.configurations.type_options.slice(1);
    labels.dialogs.manage.form.find((f) => f.name === "status")!.options = i18nManager.Labels.dashboard.ploutous.configurations.status_options.slice(1);
    labels.sided_labels.dropdown_form!.find((f) => f.control.name === "type")!.control.options = i18nManager.Labels.dashboard.ploutous.configurations.type_options;
    labels.sided_labels.dropdown_form!.find((f) => f.control.name === "status")!.control.options = i18nManager.Labels.dashboard.ploutous.configurations.status_options;

    return SidedContainer({
        searchParams,
        setSearchParams,
        labels: labels.sided_labels,
        data: countryConfigurations,
        onAction: manageCountryConfiguration,
        queryData: fetchCountryConfigurations,
        activeData: (activeCountryConfiguration ? {
            data: activeCountryConfiguration,
            element: (<React.Fragment>
                <div className="header">
                    <i className="fa fa-times" onClick={() => {
                        setActiveCountryConfiguration(undefined);
                        Utils.updateSearchParams("active_data_id", undefined, setSearchParams);
                    }} />
                    <span className="name">{activeCountryConfiguration?.key}</span>
                    <span className="id" style={{ fontWeight: "bold" }}>{activeCountryConfiguration?.external_id}</span>
                    <span className="id">{activeCountryConfiguration?.type}</span>
                    <span className="desc">{activeCountryConfiguration?.description}</span>
                </div>
                <div className="middle actions" style={{ marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>
                    <span style={{ marginTop: 5 }}><b>{i18nManager.Labels.dashboard.ploutous.configurations.type_options.find((t) => t.value === activeCountryConfiguration.type)?.label}</b></span>
                    <ConfigurationValue type={activeCountryConfiguration.type} value={activeCountryConfiguration.options} />
                </div>
                <div className="middle actions" style={{ marginBottom: 0 }}>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountryConfiguration(activeCountryConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.created_by}:</b> {activeCountryConfiguration?.created_by?.first_name} {activeCountryConfiguration?.created_by?.last_name} ({activeCountryConfiguration?.created_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountryConfiguration(activeCountryConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.updated_by}:</b> {activeCountryConfiguration?.updated_by?.first_name} {activeCountryConfiguration?.updated_by?.last_name} ({activeCountryConfiguration?.updated_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountryConfiguration(activeCountryConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.created_at}:</b> {Utils.formatToFineDate5(activeCountryConfiguration?.created_at)}</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountryConfiguration(activeCountryConfiguration)}><b>{i18nManager.Labels.dashboard.ploutous.updated_at}:</b> {Utils.formatToFineDate5(activeCountryConfiguration?.updated_at)}</span>
                </div>
                <div className="actions" style={{ overflow: "auto" }}>
                    <span className="link" onClick={() => manageCountryConfiguration(activeCountryConfiguration)}>{i18nManager.Labels.dashboard.ploutous.edit}</span>
                    {activeCountryConfiguration?.status !== "ACTIVE" && (<span className="link" onClick={() => activateDeactivateCountryConfiguration(activeCountryConfiguration, true)}>{i18nManager.Labels.dashboard.ploutous.activate}</span>)}
                    {activeCountryConfiguration?.status === "ACTIVE" && (<span className="link danger" onClick={() => activateDeactivateCountryConfiguration(activeCountryConfiguration, false)}>{i18nManager.Labels.dashboard.ploutous.deactivate}</span>)}
                    {activeCountryConfiguration?.status !== "DELETED" && (<span className="link danger" onClick={() => deleteCountryConfiguration(activeCountryConfiguration)}>{i18nManager.Labels.dashboard.ploutous.delete}</span>)}
                </div>
            </React.Fragment>)
        } : undefined),
        dataTemplate: (countryConfiguration: ICountryConfiguration) => {
            return (<div className={Classname.build("channel", (activeCountryConfiguration?.external_id === countryConfiguration.external_id ? "active" : null))} onClick={() => {
                setActiveCountryConfiguration(countryConfiguration);
                Utils.updateSearchParams("active_data_id", countryConfiguration.external_id, setSearchParams);
            }}>
                <div className="id" style={countryConfiguration.status === "DELETED" ? { textDecoration: "line-through" } : {}}>
                    <span className="name" style={{ marginRight: 5 }}>{countryConfiguration.key}</span> <span className="type">- {countryConfiguration.type}</span>
                </div>
                <span className="desc">{countryConfiguration.description}</span>
                <div className="ctrls">
                    <i className="fa fa-edit link gray" style={{ marginRight: 10 }} onClick={() => manageCountryConfiguration(countryConfiguration)} />
                    <i className={Classname.build("fa fa-trash link danger", (countryConfiguration.status === "DELETED" ? "noseur-disabled" : null))} style={{ marginRight: 10 }} onClick={() => deleteCountryConfiguration(countryConfiguration)} />
                </div>
            </div>);
        }
    });

    function fetchCountryConfigurations() {
        const id = searchParams.get("active_data_id");
        countryConfigurationAndMappingService.queryCountryConfigurations(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ICountryConfiguration>) => {
            setCountryConfigurations(apiResponse.data!);
            if (id) for (const item of apiResponse.data?.content!) {
                if (item.external_id === id) {
                    setActiveCountryConfiguration(item);
                    //manageCountryConfiguration(item);
                    break;
                }
            }
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageCountryConfiguration(_countryConfiguration: ICountryConfiguration = {} as any) {
        let changeProps: (type: ConfigurationType, v?: any) => void;
        const countryConfiguration: ICountryConfiguration = ObjectHelper.clone(_countryConfiguration);
        const basics = labels.dialogs.manage.basic[countryConfiguration ? "update" : "create"];
        const renderValueComponent = !BoolHelper.equalsAny(countryConfiguration.type, [ConfigurationType.TEXT, ConfigurationType.NUMBER]);

        const dialog = ConfirmDialog({
            ...basics,
            closable: true,
            rightStyle: { flex: 1.5 },
            confirmLabel: basics.confirm_label,
            style: { minWidth: (renderValueComponent ? 800 : 400), maxWidth: "80%" },
            content: (<div className="add-new-role" ref={(_) => {
                changeProps?.(countryConfiguration.type, countryConfiguration.options);
            }}>
                {Elements.buildConfirmDialogForm(labels.dialogs.manage.form, (name: string, value: string) => {
                    dialogErrorRef.current!.innerText = " ";
                    (countryConfiguration as any)[name] = value;
                    if (name === "type") {
                        changeProps?.(value as ConfigurationType, countryConfiguration.options);
                    }
                }, countryConfiguration, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            rightContent: (renderValueComponent ? (<div style={{ display: "flex", flexDirection: "column", height: "100%", borderLeft: "1px solid #F0F0F0", marginTop: 30, marginLeft: 5, paddingLeft: 25 }}>
                <ConfigurationInputs type={ConfigurationType.TEXT} value={countryConfiguration.options} changeProps={(cb) => changeProps = cb} onNewValue={(v) => {
                    countryConfiguration.options = v;
                }} excludes={[ConfigurationType.TEXT, ConfigurationType.NUMBER]} />
            </div>) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                let errorMessage;
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(countryConfiguration).length < 4) {
                    errorMessage = labels.dialogs.manage.fill_required_fields;
                }
                if (errorMessage) {
                    dialogErrorRef.current!.innerText = errorMessage;
                    return false;
                }
                button?.setLoadingState(true);
                const method = (!!countryConfiguration.external_id ? countryConfigurationAndMappingService.updateCountryConfiguration : countryConfigurationAndMappingService.createCountryConfiguration).bind(countryConfigurationAndMappingService);
                method(countryConfiguration)
                    .then(({ }: SanitizedResponse<ICountryConfiguration>) => {
                        Siren.alert(basics.successful);
                        dialog.hide(fetchCountryConfigurations);
                    }).catch(({ errors }: SanitizedResponse<any>) => {
                        dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                    }).finally(() => {
                        button?.setLoadingState(false);
                    });
                return false;
            }
        });
        dialog.show();
    }

    function activateDeactivateCountryConfiguration(countryConfiguration: ICountryConfiguration, activate: boolean) {
        const dialogLabels = labels.dialogs[activate ? "activate" : "deactivate"];
        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmScheme: (activate ? Schemes.RIVTN_QUIRINUS : Scheme.DANGER),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                countryConfigurationAndMappingService.updateCountryConfiguration({ external_id: countryConfiguration.external_id, status: (activate ? "ACTIVE" : "IN_ACTIVE") }).then(() => {
                    Siren.alert(dialogLabels.successful);
                    dialog.hide(() => fetchCountryConfigurations());
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

    function deleteCountryConfiguration(countryConfiguration: ICountryConfiguration) {
        const dialog = ConfirmDialog({
            closable: true,
            confirmScheme: Scheme.DANGER,
            desc: labels.dialogs.delete.desc,
            title: labels.dialogs.delete.title,
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                countryConfigurationAndMappingService.deleteCountryConfiguration(countryConfiguration.external_id).then(() => {
                    Siren.alert(labels.dialogs.delete.successful);
                    dialog.hide(() => setActiveCountryConfiguration(undefined));
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default CountryConfigurations;

